import { EUserProfileValues } from "../utilities/enums";
import OpenSessionError from "./errors/authenticationAuthorizationError";
import { EErrorOrigin, EOpenSessionError } from "./../utilities/enums";

/**
 * @description This class provides the functionality to:
 *
 * - Create and manage the UUID
 * - Perform an openSession call and keep track of the SessionManager
 *
 * The constructor will make sures there is a UUID, and start the session request call async
 */
export default class SessionManager {
  userInfo = null;
  session = null;
  version = "1.0.0";
  enabledFeatures = null;
  getSessionPromiseResolve = null;
  getSessionPromiseReject = null;
  isPreviewUser = false;
  _apiManager = null;
  _cookieManager = null;

  constructor(apiManager, cookieManager, options) {
    this._apiManager = apiManager;
    this._cookieManager = cookieManager;
    this._options = options;
    // check if there is a cookie
    this.userInfo = this._cookieManager.getCookie(
      this._cookieManager.cookieKeys.UUID
    );
    if (!this.userInfo) {
      // try backward compatibility cookie
      this.userInfo = this._cookieManager.getCookie(
        this._cookieManager.cookieKeys.UUIDV1
      );
      if (!this.userInfo) {
        // create a uuid and store it in userinfo
        this.userInfo = {
          UUID: createUUID(),
          userId: 0,
        };
      } else if (this.userInfo.indexOf("UUID") === -1) {
        // for backward compatibility we accept the fact that it might containing
        // only a string and not an object
        this.userInfo = {
          UUID: this.userInfo,
          userId: 0,
        };
      } else {
        // parse out the object from the string
        this.userInfo = JSON.parse(this.userInfo);
      }
      // store the cookie with the latest info
      this._cookieManager.createCookie(
        this._cookieManager.cookieKeys.UUID,
        JSON.stringify(this.userInfo)
      );
    } else {
      // parse out the object from the string
      this.userInfo = JSON.parse(this.userInfo);
    }
    // trigger the getSession, it is always needed in any case, so it might be
    // that the next getSessionInfo call is successful immediately.
    this.getSessionInfo();
  }

  get UUID() {
    return this.userInfo.UUID;
  }

  get userId() {
    return this.userInfo ? this.userInfo.userId : 0;
  }

  /**
   * @description Returns a promises that can be used to chain the data,
   * it returns the SessionId but the full data can be retrieved in the sessionManager later in case the sessionManager already contains a session,
   * it will not trigger again, unless you provide the force parameter
   *
   * Probably need to check if the session got expired or not would be better
   * @param {boolean} force
   * @returns {Promise}
   */
  getSessionInfo(force = false) {
    if (this.getSessionPromise) {
      return this.getSessionPromise;
    }
    this.getSessionPromise = new Promise((resolve, reject) => {
      this.getSessionPromiseReject = reject;
      this.getSessionPromiseResolve = resolve;
      if (!force && this.session != null) {
        this.getSessionPromiseResolve(this.session.SessionInfo.SessionId);
      } else {
        this._apiManager
          .openSession(this.version, this.userId, "1.0.0", this.UUID)
          .then((session) => {
            if (
              typeof session.ReturnCode === "string" &&
              session.ReturnCode.toLowerCase() === "error"
            ) {
              this.getSessionPromiseReject(
                new OpenSessionError(
                  session,
                  EErrorOrigin.OPENSESSION,
                  EOpenSessionError.UNSUCCESSFULL
                )
              );
            } else if (!session.SessionInfo) {
              this.getSessionPromiseReject(
                new OpenSessionError(
                  session,
                  EErrorOrigin.OPENSESSION,
                  EOpenSessionError.NO_SESSION_INFO
                )
              );
            } else {
              this.session = session;
              this.userInfo.userId = this.session.SessionInfo.UserId;
              this._cookieManager.createCookie(
                this._cookieManager.cookieKeys.UUID,
                JSON.stringify(this.userInfo)
              );

              if (this.session.UserData.UserProfiles) {
                const enabledFeature =
                  this.session.UserData.UserProfiles.filter(
                    (userProfile) =>
                      userProfile.Key == EUserProfileValues.ENABLED_FEATURES
                  );
                if (enabledFeature && enabledFeature[0]) {
                  try {
                    this.enabledFeatures = JSON.parse(enabledFeature[0].Value);
                  } catch (e) {
                    this.enabledFeatures = null;
                  }
                }
              }

              if (this.session.UserData.Preview) {
                this.isPreviewUser = this.session.UserData.Preview;
              }

              this.getSessionPromiseResolve(this.session.SessionInfo.SessionId);
            }
          })
          .catch((error) => {
            this.getSessionPromiseReject(
              new OpenSessionError(
                error,
                EErrorOrigin.OPENSESSION,
                EOpenSessionError.CALL_FAILED
              )
            );
          });
      }
    });
    return this.getSessionPromise;
  }

  isFeatureEnabled(feature) {
    //check if the feature is in the list of featured that should be enabled via the backend
    if (this._options.featuresToBeEnabledByBE.indexOf(feature) >= 0) {
      //check if the feature is returned by the backend
      if (this.enabledFeatures && this.enabledFeatures[feature] != undefined) {
        //return the feature value from the backend
        return this.enabledFeatures[feature];
      } else {
        //In case the client support this feature, if the feature is not defined in the backend, the default behavior should be to disable the feature.
        return false;
      }
    }
    //by default the feature is enabled
    return true;
  }
}

///////////////
// internal functions, not exported, global to this module
/////////////
/* eslint-disable */

function createUUID() {
  // Generate new guId
  const guId = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    let r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
  return guId;
}



// WEBPACK FOOTER //
// ./src/common/utilities/sessionmanager.js